import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Database, Eye, EyeOff } from 'lucide-react';
import './Login.css';
import { clearSession, getToken, setSession, validateSession } from './auth';
import { validateUsername, validatePassword } from './utils/validation';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
axios.defaults.baseURL = API_ROOT;

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(Boolean(getToken()));
  const [alreadyAuthed, setAlreadyAuthed] = useState(false);

  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!getToken()) {
        if (!cancelled) setChecking(false);
        return;
      }
      const user = await validateSession();
      if (cancelled) return;
      if (user) {
        setAlreadyAuthed(true);
      } else {
        clearSession();
      }
      setChecking(false);
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const completeLogin = (data) => {
    setSession({
      token: data.token,
      role: data.role,
      username: data.username,
      userId: data.userId
    });
    setPassword('');
    setOtpCode('');
    toast.success('Login successful');
    window.location.replace('/dashboard');
  };

  const handleOtpRequest = async (e) => {
    e.preventDefault();
    const usernameError = validateUsername(username);
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    try {
      setLoading(true);
      clearSession();
      const res = await axios.post('/api/auth/otp/request', {
        identifier: username.trim(),
        password
      });
      setOtpSent(true);
      setMaskedEmail(res.data.maskedEmail || 'your registered email');
      setOtpCode('');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    if (!username.trim() || !otpCode.trim()) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    try {
      setLoading(true);
      clearSession();
      const res = await axios.post('/api/auth/otp/verify', {
        identifier: username.trim(),
        otp: otpCode.trim()
      });
      completeLogin(res.data);
    } catch (err) {
      clearSession();
      toast.error(err?.response?.data?.error || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="login-wrap">
        <div className="login-body">
          <p className="login-subtitle">Checking session…</p>
        </div>
      </div>
    );
  }

  if (alreadyAuthed) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="login-wrap">
      <ToastContainer theme="light" position="top-right" autoClose={3000} />
      <header className="topbar login-topbar">
        <h1 className="topbar-brand">
          <Database size={24} />
          <span>Client Management</span>
        </h1>
        <Link to="/" className="login-nav-back">
          ← Back to home
        </Link>
      </header>
      <div className="login-body">
        <div className="login-card">
          <h2 className="login-title">Welcome back</h2>
          <p className="login-subtitle">
            {otpSent ? 'Enter the OTP sent to your registered email' : 'Enter username and password to receive an OTP'}
          </p>

          <form onSubmit={otpSent ? handleOtpVerify : handleOtpRequest} autoComplete="off">
              <div className="form-group">
                <label className="form-label" htmlFor="username">Username</label>
                <input
                  id="username"
                  className="form-control"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                  placeholder="Enter username"
                  autoComplete="username"
                  minLength={3}
                  maxLength={50}
                  pattern="[a-zA-Z0-9._-]{3,50}"
                  title="3–50 characters: letters, numbers, dots, hyphens, underscores"
                  required
                  disabled={otpSent}
                />
                <small className="field-hint">3–50 characters · letters, numbers, . _ -</small>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="password">Password</label>
                <div className="login-password-row">
                  <input
                    id="password"
                    className="form-control"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    minLength={6}
                    maxLength={128}
                    required
                    disabled={otpSent}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPwd((v) => !v)}
                    disabled={otpSent}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {otpSent && (
                <>
                  <p className="login-otp-hint">
                    OTP sent to <strong>{maskedEmail}</strong>. Code expires in 10 minutes.
                  </p>
                  <div className="form-group">
                    <label className="form-label" htmlFor="otp-code">6-digit OTP</label>
                    <input
                      id="otp-code"
                      className="form-control login-otp-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter OTP"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                </>
              )}

              <div className="login-actions">
                {otpSent ? (
                  <button type="submit" disabled={loading} className="btn btn-primary login-actions-single">
                    {loading ? 'Verifying…' : 'Verify & sign in'}
                  </button>
                ) : (
                  <button type="submit" disabled={loading} className="btn btn-primary login-actions-single">
                    {loading ? 'Sending OTP…' : 'Send OTP'}
                  </button>
                )}
              </div>

              {otpSent && (
                <p className="login-otp-resend">
                  <button
                    type="button"
                    className="login-otp-resend-btn"
                    disabled={loading}
                    onClick={handleOtpRequest}
                  >
                    Resend OTP
                  </button>
                </p>
              )}
              {!otpSent && (
                <p className="login-forgot">
                  <Link to="/forgot-password">Forgot password?</Link>
                </p>
              )}
            </form>
        </div>
      </div>
    </div>
  );
}
